import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  Divider,
  Box,
} from '@mui/material';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import SettingField from '../entities/configuration/sectionField';

export interface BlueprintPromptSetting {
  entityIndex: number;
  entityName: string;
  entityDefId: string;
  setting: any;
}

interface Props {
  open: boolean;
  promptSettings: BlueprintPromptSetting[];
  onCancel: () => void;
  onSubmit: (values: Record<number, Record<string, any>>) => void;
}

const BlueprintSettingsDialog = ({ open, promptSettings, onCancel, onSubmit }: Props) => {
  const { t } = useTranslation();
  const [tempSettings, setTempSettings] = useState<Record<number, Record<string, any>>>({});
  const [errors, setErrors] = useState<Record<number, Record<string, string>>>({});

  useEffect(() => {
    if (open) {
      const initial: Record<number, Record<string, any>> = {};
      promptSettings.forEach((ps) => {
        if (!initial[ps.entityIndex]) {
          initial[ps.entityIndex] = {};
        }
        initial[ps.entityIndex][ps.setting.name] = '';
      });
      setTempSettings(initial);
      setErrors({});
    }
  }, [open, promptSettings]);

  const handleFieldChange = (entityIdx: number, settingName: string, value: any) => {
    setTempSettings((prev) => ({
      ...prev,
      [entityIdx]: {
        ...(prev[entityIdx] || {}),
        [settingName]: value,
      },
    }));

    if (value !== undefined && value !== null && value !== '') {
      setErrors((prev) => {
        const copy = { ...prev };
        if (copy[entityIdx]) {
          delete copy[entityIdx][settingName];
        }
        return copy;
      });
    }
  };

  const handleCreate = () => {
    let hasError = false;
    const newErrors: Record<number, Record<string, string>> = {};

    promptSettings.forEach((ps) => {
      const val = tempSettings[ps.entityIndex]?.[ps.setting.name];
      if (val === undefined || val === null || val === '') {
        hasError = true;
        if (!newErrors[ps.entityIndex]) {
          newErrors[ps.entityIndex] = {};
        }
        newErrors[ps.entityIndex][ps.setting.name] = t('common.errors.requiredField', 'Required field');
      }
    });

    if (hasError) {
      setErrors(newErrors);
      return;
    }

    onSubmit(tempSettings);
  };

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="md" fullWidth>
      <DialogTitle>Configure Blueprint Settings</DialogTitle>
      <Divider />
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          The following settings are required to initialize the pipeline components from the blueprint.
        </DialogContentText>
        <Grid container spacing={2.5} sx={{ mt: 0.5 }}>
          {promptSettings.map((ps, idx) => {
            const modifiedSettingDef = {
              ...ps.setting,
              label: `${ps.entityName} - ${ps.setting.label}`,
            };

            return (
              <SettingField
                key={`${ps.entityIndex}_${ps.setting.name}`}
                entityDefId={ps.entityDefId}
                settingDef={modifiedSettingDef}
                settings={tempSettings[ps.entityIndex] || {}}
                errors={errors[ps.entityIndex] || {}}
                onChange={(name, val) => handleFieldChange(ps.entityIndex, name, val)}
                index={idx}
              />
            );
          })}
        </Grid>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onCancel}>{t('common.buttons.cancel', 'Cancel')}</Button>
        <Box sx={{ flexGrow: 1 }} />
        <Button onClick={() => onSubmit(tempSettings)} color="secondary" variant="outlined" sx={{ mr: 1 }}>
          {t('common.buttons.fillLater', 'Fill Later')}
        </Button>
        <Button onClick={handleCreate} color="primary" variant="contained">
          {t('common.buttons.create', 'Create Pipeline')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BlueprintSettingsDialog;
